import React, { FC, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import useConfirmDialog from '@/components/ConfirmDialogProvider';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import Container from '@/components/Container';
import {
  Icon,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  FormControlLabel,
  ListItemAvatar,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ToolbarHeader } from '@/components/ToolbarHeader';
import { useTranslation } from 'react-i18next';
import { Api } from '@/services/Api';
import { useStore } from '@/components/store/Store';
import MediaList from '@/components/MediaList/MediaList';
import { GenericMedia } from '@/declarations/GenericMedia';
import { Card } from '@/declarations/models/Card';
import { ViewType } from '@/declarations/ViewType';
import { useApi } from '@/hooks/useApi';
import { TagType } from '@/declarations/models/TagType';
import Styles from '@/assets/js/Styles';
import PlaceholderImage from '@/assets/images/broken_image.png';
import Grid from '@mui/material/Grid';
import { noWrapTextStyle } from '@/views/MediaView/MediaViewTypes/ListViewItem';
import { Skin } from '@/declarations/models/Skin';
import { SortDirection, SortOption, SortType } from '@/declarations/models/SortOption';
import FullScreenModal from '@/components/FullScreenModal';
import CardEditor from '@/editor/CardEditor/CardEditor';
import { BlockType } from '@/declarations/models/BlockType';
import { Status } from '@/declarations/models/Status';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { SortSelector } from '@/components/SortSelector';
import { TagsAutocomplete } from '@/components/TagsAutocomplete';
import { Tag } from '@/declarations/models/Tag';
import { PageStatusCircle } from '@/components/PageStatusCircle';

type BlocksSortOptions = SortType.TITLE | SortType.UPDATED_AT;

const CardCategoryBadge: FC<{ category: string }> = ({ category }) => {
  return <Chip label={category} color='secondary' size='small' />;
};

export const CardsView: FC<ChildrenProp> = () => {
  const store = useStore();
  const siteId = store?.state?.selectedSite?.id || 0;
  const selectedLocale = store?.state?.selectedSiteLanguage || 'no';
  const skins = store?.state?.selectedSiteSkins || ([] as Array<Skin>);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirmDialog();
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const [query, setQuery] = useState<string>('');
  const [sortOption, setSortOption] = useState<SortOption>({ sortBy: SortType.CREATED_AT, order: SortDirection.DESC });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [cards, setCards] = useState<Array<GenericMedia<Card>>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<{ start: number; rows: number }>({ start: 0, rows: 20 });

  const [selectedBlockType, setSelectedBlockType] = useState<BlockType.CARD | BlockType.PERSON>(BlockType.CARD);
  const [selectedCategories, setSelectedCategories] = useState<Array<Tag>>([]);
  const [allTags, , refreshTags] = useApi(() => Api.getAllTagsForSite(siteId || 0, {}), []);
  const categories = allTags.filter((tag) => tag.content?.isCardCategory === true && tag.type === TagType.CATEGORY);
  useEffect(() => {
    refreshTags();
    setSelectedCategories([]);
  }, [refreshTags, siteId]);

  const [cardId, setCardId] = useState<number>();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [cardFilter, setCardFilter] = useState<'internal_card' | 'external_card' | null>(null);
  const [showUnpublished, setShowUnpublished] = useState<boolean>(false);
  const [forceRefresh, setForceRefresh] = useState({} as unknown);
  const isSuperAdmin = store?.state?.currentUser?.user?.is_super_admin;

  const handlePageChanged = (changes: Partial<typeof page>) => {
    setPage((prevPage) => ({ ...prevPage, ...changes }));
  };

  const onAfterSave = () => {
    setForceRefresh({});
  };

  const handleItemDelete = async (item: GenericMedia<Card>) => {
    const id = Number(item.id);
    if (!Number.isNaN(id) && (await confirm(tComponents('MediaView.ConfirmDelete')))) {
      const [, error] = await Api.deleteBlock(siteId, id).fetch();
      if (error) enqueueSnackbar(error.message, { variant: 'error' });
      else enqueueSnackbar('Deleted item', { variant: 'success' });
      setForceRefresh({});
    }
  };

  const onItemClick = (item: GenericMedia<Card>) => {
    setCardId(Number(item.id));
    setEditModalOpen(true);
  };

  const onCreateCardClick = () => {
    setCardId(undefined);
    setEditModalOpen(true);
  };

  const getSkin = (item: Card): Skin | undefined => skins.find((s) => s.class === item.content?.skin);

  useEffect(() => {
    let unmounted = false;
    const ctx = Api.getBlocks(siteId, {
      ...page,
      categories: selectedCategories.map((cat) => cat.tag).join(','),
      order: sortOption.order,
      order_by: sortOption.sortBy as BlocksSortOptions,
      locale: selectedLocale,
      block_type: selectedBlockType || undefined,
      text_query: query,
      ...(showUnpublished && cardFilter === 'internal_card' && { unpublished: true }),
      ...(cardFilter === 'internal_card' && { internal_card: true }),
      ...(cardFilter === 'external_card' && { external_card: true }),
    });
    setIsLoading(true);
    ctx
      .fetchDirect(null)
      .then((res) => {
        if (res) {
          setTotalCount(res.totalCount || 0);
          setCards(
            res.items.map(
              (item) =>
                ({
                  id: String(item.id || 0),
                  title: '', // title is rendered by custom render function
                  url: item.content?.mediaobject?.url,
                  mimetype: item.content?.mediaobject?.mimetype,
                  focusPoint: item.content?.mediaobject?.local,
                  source: item,
                } as GenericMedia<Card>),
            ),
          );
        }
      })
      .finally(() => {
        if (!unmounted) {
          setIsLoading(false);
        }
      });

    return () => {
      unmounted = true;
      ctx.abort();
    };
  }, [
    page,
    query,
    selectedCategories,
    siteId,
    sortOption,
    forceRefresh,
    selectedLocale,
    selectedBlockType,
    cardFilter,
    showUnpublished,
  ]);

  const renderListItemContent = (item: GenericMedia<Card>) => (
    <Grid container sx={{ width: '100%' }}>
      <Grid sx={{ xs: 1 }}>
        <ListItemAvatar>
          <Avatar src={item.url || PlaceholderImage} variant='square' />
        </ListItemAvatar>
      </Grid>
      <Grid sx={{ xs: 9, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
            {item.source?.title}
          </Typography>
          {item.source?.page?.title && (
            <Container ml={4}>
              <PageStatusCircle status={item.source.page.status} />
              <Typography
                variant='caption'
                color={item.source.page.status === Status.PUBLISHED ? 'green' : 'textSecondary'}>
                {item.source.page.title}
              </Typography>
            </Container>
          )}
        </Box>
        <Box>
          <Typography variant='body2' sx={noWrapTextStyle}>
            {item.source?.description}
          </Typography>
        </Box>
      </Grid>
      <Grid sx={{ xs: 2 }}>
        {item.source?.content?.category?.tag && <CardCategoryBadge category={item.source.content.category.tag} />}
      </Grid>
    </Grid>
  );

  const renderGridItemContent = (item: GenericMedia<Card>) => (
    <Container
      fullWidth
      column
      left
      gap={0}
      sx={{
        // Fills the card down to the height of the tallest in the row
        flex: 1,
      }}>
      <ButtonBase
        tabIndex={-1}
        sx={{
          width: '100%',
          display: 'block',
        }}
        onClick={() => onItemClick(item)}>
        <Box
          sx={{
            p: 2,
            textAlign: 'left',
            position: 'relative',
            backgroundColor: Styles.Colors.WHITE,
          }}>
          {/* The card's skin as a dot at the lower left of the image */}
          {getSkin(item.source)?.hex && (
            <Tooltip title={getSkin(item.source)?.title}>
              <Box
                aria-label={getSkin(item.source)?.title}
                sx={{
                  position: 'absolute',
                  top: '-32px',
                  left: '8px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: getSkin(item.source)?.hex,
                  border: `2px solid ${Styles.Colors.WHITE}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
              />
            </Tooltip>
          )}
          <div style={{ position: 'absolute', top: '-30px', right: '4px', display: 'flex', gap: '4px' }}>
            {item.source?.content?.category?.tag && <CardCategoryBadge category={item.source.content.category.tag} />}
            {item.source.content?.url && <CardCategoryBadge category={tComponents('CardEditor.UrlExternal')} />}
          </div>

          {/* Room for two lines whether the title needs one or two, longer ones cut short */}
          <Typography
            variant='body1'
            title={item.source?.title}
            sx={{
              fontWeight: 'bold',
              lineHeight: 1.4,
              minHeight: '2.8em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {item.source?.title}
          </Typography>
        </Box>
      </ButtonBase>
      <Container
        fullWidth
        left
        column
        spaceBetween
        wrap
        p={1}
        gap={0}
        sx={() => ({
          borderTop: '1px solid rgba(200,200,200,0.25)',
          minHeight: '7.5em',
          flexGrow: 1,
          gap: 0,
          backgroundColor: '#fff',
        })}>
        {item.source?.page?.title && (
          <Container ml={0} sx={{ background: '#fff', padding: '3px', borderRadius: '3px' }}>
            <PageStatusCircle status={item.source.page.status} />
            <Typography
              variant='caption'
              color={item.source.page.status === Status.PUBLISHED ? 'green' : 'textSecondary'}>
              {item.source.page.title}
            </Typography>
          </Container>
        )}
        {item.source.content?.url && (
          <Container ml={0} gap={1} top>
            <Icon sx={{ marginLeft: '2px', width: '25px' }}>
              <MaterialSymbol name='link' />
            </Icon>
            <a
              target='_blank'
              rel='noreferrer'
              href={item.source.content?.url || '#'}
              style={{ lineHeight: '1', textDecoration: 'none', padding: '3px' }}>
              <Typography
                sx={() => ({
                  fontSize: '11px',
                  lineHeight: '1',
                  textDecoration: 'underline',
                  wordBreak: 'break-all',
                })}
                variant='caption'>
                {(item.source.content?.url || '#').substring(0, 60)}..
              </Typography>
            </a>
          </Container>
        )}
        <Button
          sx={{ marginLeft: 'auto' }}
          onClick={() => handleItemDelete?.(item)}
          endIcon={<MaterialSymbol name='delete' fill />}>
          {tCommon('delete')}
        </Button>
      </Container>
    </Container>
  );

  return (
    <Container p={6} pb={0} fullWidth fullHeight column gap={0} top>
      <FullScreenModal modalOpen={editModalOpen}>
        <CardEditor
          siteId={siteId}
          cardId={cardId}
          onCloseEditor={() => setEditModalOpen(false)}
          skins={skins}
          categories={categories}
          onAfterSave={onAfterSave}
          defaultLocale={selectedLocale}
        />
      </FullScreenModal>
      <ToolbarHeader
        renderSearchField={({ query: searchBarQuery, onQueryChange: onSearchBarQueryChange }) => (
          <TagsAutocomplete
            label={tCommon('searchAndCategories')}
            tagType={TagType.CATEGORY}
            siteId={siteId}
            query={searchBarQuery}
            onQueryChange={onSearchBarQueryChange}
            tags={selectedCategories}
            onTagsChange={setSelectedCategories}
          />
        )}
        query={query}
        onQueryChange={setQuery}
        heading={
          <Typography variant='h3' sx={{ component: 'h1', fontSize: 30 }}>
            {tComponents('CardsView.Title')}
          </Typography>
        }
        topRight={
          <Box>
            <Button
              variant='contained'
              color='success'
              startIcon={<MaterialSymbol name='add' />}
              onClick={onCreateCardClick}
              type='button'>
              {tComponents('CardsView.CreateCard')}
            </Button>
          </Box>
        }
        bottomRight={
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, position: 'relative' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              {isSuperAdmin && (
                <div
                  className='superadmin'
                  style={{ display: 'none', backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
                  <RadioGroup
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                    }}
                    value={selectedBlockType}
                    onChange={(e) => {
                      const newBlockType = e.target.value as BlockType.CARD | BlockType.PERSON;
                      setSelectedBlockType(newBlockType);
                      if (newBlockType !== BlockType.CARD) {
                        setCardFilter(null);
                        setShowUnpublished(false);
                      }
                    }}>
                    {[BlockType.CARD, BlockType.PERSON].map((blockType) => (
                      <FormControlLabel
                        key={blockType || 'all'}
                        control={<Radio />}
                        value={blockType}
                        label={tComponents(`CardsView.BlockType.${blockType}`)}
                      />
                    ))}
                  </RadioGroup>
                </div>
              )}
            </Box>

            {selectedBlockType === BlockType.CARD && (
              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0 }}>
                <RadioGroup
                  sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
                  value={cardFilter ?? 'all'}
                  onChange={(e) => {
                    const val = e.target.value;
                    const newFilter = val === 'all' ? null : (val as 'internal_card' | 'external_card');
                    setCardFilter(newFilter);
                    if (newFilter !== 'internal_card') {
                      setShowUnpublished(false);
                    }
                  }}>
                  {(['all', 'internal_card', 'external_card'] as const).map((filter) => (
                    <FormControlLabel
                      key={filter}
                      control={<Radio />}
                      value={filter}
                      label={tComponents(`CardsView.CardFilter.${filter}`)}
                    />
                  ))}
                </RadioGroup>
                {cardFilter === 'internal_card' && (
                  <div
                    style={{
                      display: 'none',
                      alignItems: 'center',
                      marginTop: '8px',
                      position: 'absolute',
                      top: '35px',
                      zIndex: 2,
                    }}>
                    <Typography sx={{ fontWeight: 'bold', mr: 2 }}>Status:</Typography>

                    <RadioGroup
                      sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
                      value={showUnpublished ? 'unpublished' : 'all'}
                      onChange={(e) => setShowUnpublished(e.target.value === 'unpublished')}>
                      <FormControlLabel
                        control={<Radio />}
                        value='all'
                        label={tComponents('CardsView.CardFilter.all')}
                      />
                      <FormControlLabel
                        control={<Radio />}
                        value='unpublished'
                        label={tComponents('CardsView.CardFilter.unpublished')}
                      />
                    </RadioGroup>
                  </div>
                )}
              </Box>
            )}
          </Box>
        }
        boxProps={{
          sx: {
            alignSelf: 'stretch',
          },
        }}
      />

      {selectedBlockType === BlockType.PERSON && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', width: '100%', mt: 2 }}>
          <Box sx={{ width: '100%', mt: 2 }}>
            <Box
              sx={{
                display: 'inline-block',
                p: 2,
                borderRadius: 1,
                border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
                bgcolor: Styles.Colors.LIGHT_BLUE,
              }}>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: tComponents(`CardsView.PersonsInfo`) }} />
            </Box>
          </Box>
        </Box>
      )}

      <MediaList
        customHeaderElement={
          <SortSelector
            availableOptions={[SortType.TITLE, SortType.CREATED_AT]}
            setSortOption={setSortOption}
            sortOption={sortOption}
          />
        }
        contentOverflow='visible'
        renderListItemContent={renderListItemContent}
        renderGridItemContent={renderGridItemContent}
        headerSx={{}}
        onPageChange={(_, start, rows) => handlePageChanged({ start, rows })}
        isLoading={isLoading}
        items={cards}
        page={page}
        totalItemCount={totalCount}
        onItemClick={(item) => onItemClick(item)}
        isSelectable={false}
        defaultSelectedViewType={ViewType.GRID}
        variant='card'
      />
    </Container>
  );
};

export default CardsView;
